/**
 * usePropertySearchKeyboard — arrow-key navigation over the property search
 * results.
 *
 * Focus stays in the SEARCH INPUT throughout, which is the combobox pattern: the
 * input keeps the caret so typing never breaks, and the highlighted row is named
 * to assistive technology by `aria-activedescendant` rather than by moving focus
 * into the list. Moving focus row to row would have taken the caret out of the
 * box the client is still typing in.
 *
 * Wrapping is deliberate — the list is short (5 rows on desktop, 3 on mobile) and
 * wrapping is what a client expects from a search dropdown; a hard stop at the
 * ends reads as a broken key.
 */

import { useCallback, useEffect, useState } from 'react'

export const PROPERTY_OPTION_ID_PREFIX = 'pf-property-option'

/** The DOM id of one result row, so the input can point at it. */
export const propertyOptionId = (index: number): string => `${PROPERTY_OPTION_ID_PREFIX}-${index}`

interface Input {
  /** How many rows the dropdown is showing right now. */
  optionCount: number
  /** Whether the dropdown is open at all. */
  isOpen: boolean
  /** Commit the highlighted row. */
  onSelect: (index: number) => void
}

export interface PropertySearchKeyboard {
  /** Index of the highlighted row, or -1 when nothing is highlighted. */
  activeIndex: number
  /** Highlight a row without committing it (pointer hover keeps the two in step). */
  setActiveIndex: (index: number) => void
  /** Attach to the search input. */
  onKeyDown: (event: { key: string, preventDefault: () => void, stopPropagation: () => void }) => void
  /** `aria-activedescendant` for the input, or undefined when nothing is highlighted. */
  activeDescendantId: string | undefined
}

export const usePropertySearchKeyboard = ({ optionCount, isOpen, onSelect }: Input): PropertySearchKeyboard => {
  const [activeIndex, setActiveIndex] = useState(-1)

  // Nothing starts highlighted, and a changed result set must not leave the
  // highlight pointing at a row that has moved or gone — Enter would then commit
  // a property the client never saw highlighted.
  useEffect(() => {
    setActiveIndex(-1)
  }, [optionCount, isOpen])

  const onKeyDown = useCallback((event: { key: string, preventDefault: () => void, stopPropagation: () => void }) => {
    if (!isOpen || optionCount === 0) return

    switch (event.key) {
      case 'ArrowDown':
        // Without this the caret jumps to the end of the search text on every press.
        event.preventDefault()
        setActiveIndex((current) => (current + 1) % optionCount)
        return
      case 'ArrowUp':
        event.preventDefault()
        setActiveIndex((current) => (current <= 0 ? optionCount - 1 : current - 1))
        return
      case 'Enter':
        // Enter with nothing highlighted is left alone: the client is typing, not
        // choosing, and swallowing it would make the key feel dead. Read from the
        // closure rather than from a `setActiveIndex` updater — an updater is not
        // the place for a side effect, and StrictMode runs it twice.
        if (activeIndex >= 0 && activeIndex < optionCount) onSelect(activeIndex)
        return
      case 'Escape':
        // A highlighted result takes the first Escape; the drawer around the search closes on the next.
        if (activeIndex < 0) return
        event.preventDefault()
        event.stopPropagation()
        setActiveIndex(-1)
    }
  }, [isOpen, optionCount, onSelect, activeIndex])

  return {
    activeIndex,
    setActiveIndex,
    onKeyDown,
    activeDescendantId: activeIndex >= 0 ? propertyOptionId(activeIndex) : undefined,
  }
}
